import type { ProjectCockpitDisplayViewModel } from "./viewmodel/projectCockpitAdapter";

export type ActiveSurface =
  | "dashboard"
  | "admin"
  | "create"
  | "specify"
  | "project"
  | "summary";

export type ScreenId =
  | "overview"
  | "inputs"
  | "documents"
  | "viewer"
  | "tender"
  | "requirements"
  | "spec"
  | "quote"
  | "compare"
  | "copilot"
  | "agentTrace"
  | "tokenUsage"
  | "decisions"
  | "risks"
  | "scope"
  | "mapping"
  | "export";

export interface NavItem {
  id: ScreenId;
  label: string;
  badge?: string;
}

export const baseNavItems: NavItem[] = [
  { id: "overview", label: "Project overview" },
  { id: "inputs", label: "Inputs" },
  { id: "documents", label: "Documents & Take-offs" },
  { id: "viewer", label: "Document Viewer" },
  { id: "requirements", label: "Requirements" },
  { id: "spec", label: "Spec workspace" },
  { id: "quote", label: "Quote" },
  { id: "risks", label: "Risks & clarifications" },
  { id: "scope", label: "Scope" },
  { id: "export", label: "Specification export" }
];

export const secondaryNavItems: NavItem[] = [
  { id: "tender", label: "Tender ingestion" },
  { id: "copilot", label: "Live co-pilot" },
  { id: "agentTrace", label: "Agent trace" },
  { id: "tokenUsage", label: "Token usage", badge: "Internal" },
  { id: "decisions", label: "Decisions" },
  { id: "mapping", label: "Mapping & commissioning" }
];

export const screenLabel = (screen: ScreenId): string =>
  baseNavItems.find((item) => item.id === screen)?.label ??
  secondaryNavItems.find((item) => item.id === screen)?.label ??
  screen;

const navItemsWithCockpitBadges = (
  items: readonly NavItem[],
  cockpit: ProjectCockpitDisplayViewModel
): NavItem[] =>
  items.map((item) => {
    if (item.id === "decisions" && cockpit.needsUserDecisionCount > 0) {
      return { ...item, badge: String(cockpit.needsUserDecisionCount) };
    }

    if (item.id === "risks" && cockpit.genuineRiskCount > 0) {
      return { ...item, badge: String(cockpit.genuineRiskCount) };
    }

    return item;
  });

export const navItemsForCockpit = (
  cockpit: ProjectCockpitDisplayViewModel
): NavItem[] => navItemsWithCockpitBadges(baseNavItems, cockpit);

export const secondaryNavItemsForCockpit = (
  cockpit: ProjectCockpitDisplayViewModel
): NavItem[] => navItemsWithCockpitBadges(secondaryNavItems, cockpit);

export const progressSteps: { id: ScreenId; label: string }[] = [
  { id: "inputs", label: "Inputs" },
  { id: "requirements", label: "Requirements" },
  { id: "spec", label: "Spec" },
  { id: "risks", label: "Review" },
  { id: "scope", label: "Scope" },
  { id: "export", label: "Export" }
];

export const progressMilestoneByScreen: Partial<Record<ScreenId, ScreenId>> = {
  inputs: "inputs",
  tender: "inputs",
  documents: "requirements",
  requirements: "requirements",
  spec: "spec",
  quote: "scope",
  compare: "requirements",
  copilot: "spec",
  agentTrace: "spec",
  tokenUsage: "spec",
  decisions: "risks",
  risks: "risks",
  scope: "scope",
  mapping: "scope",
  export: "export"
};
